import { Box, Chip, IconButton, Typography } from "@mui/material";
import {
  BookOpen,
  Gift,
  Guitar,
  Image as ImageIcon,
  Mail,
  Music,
  Newspaper,
  Plus,
  Type as TypeIcon,
  Video,
  X,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddablePageType } from "../utils/createTaasCardPages";


const PAGE_TYPE_LABEL_KEYS: Record<AddablePageType, string> = {
  Youtube: "attic_page_type_youtube",
  Image: "attic_page_type_image",
  Video: "attic_page_type_video",
  Text: "attic_page_type_text",
  Audio: "attic_page_type_audio",
  Post_Card: "attic_page_type_postcard",
  News: "attic_page_type_news",
  About: "attic_page_type_about",
  Bonus: "attic_page_type_bonus",
  Instrument: "attic_page_type_instrument",
};

const PAGE_TYPE_ICONS: Record<AddablePageType, LucideIcon> = {
  Youtube: Youtube,
  Image: ImageIcon,
  Video: Video,
  Text: TypeIcon,
  Audio: Music,
  Post_Card: Mail,
  News: Newspaper,
  About: BookOpen,
  Bonus: Gift,
  Instrument: Guitar,
};

interface AddTaasCardPageSectionProps {
  types: AddablePageType[];
  onAdd: (type: AddablePageType) => void;
  darkMode: boolean;
  colors: { title: string; body: string };
}

/** Dotted-border slot rendered between the last editable page and the
 *  ending-image page. Collapsed = a `+` button; expanded = chips for each
 *  addable page type with a `×` to collapse again. Selecting a chip adds the
 *  page above this slot and the `+` shifts down to the new bottom. */
export function AddTaasCardPageSection({
  types,
  onAdd,
  darkMode,
}: AddTaasCardPageSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const chipMeta = useMemo(
    () =>
      Object.fromEntries(
        (Object.keys(PAGE_TYPE_LABEL_KEYS) as AddablePageType[]).map((type) => [
          type,
          {
            label: t(PAGE_TYPE_LABEL_KEYS[type], { ns: "attic" }),
            Icon: PAGE_TYPE_ICONS[type],
          },
        ])
      ) as Record<AddablePageType, { label: string; Icon: LucideIcon }>,
    [t]
  );

  if (types.length === 0) return null;

  const handleSelect = (type: AddablePageType) => {
    onAdd(type);
    setExpanded(false);
  };

  const primary = darkMode ? "var(--primary-400)" : "var(--primary-600)";
  const primaryStrong = darkMode ? "var(--primary-300)" : "var(--primary-700)";
  const primarySoftBg = darkMode
    ? "rgba(99, 102, 241, 0.08)"
    : "var(--primary-100-light, var(--primary-100))";
  const primaryBorderSoft = darkMode
    ? "var(--primary-700)"
    : "var(--primary-300)";

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `2px dashed ${primary}`,
        bgcolor: primarySoftBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
      }}
    >
      {!expanded ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label={t("attic_add_page", { ns: "attic" })}
          onClick={() => setExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(true);
            }
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.9,
            borderRadius: 999,
            cursor: "pointer",
            color: "var(--neutralWhite)",
            bgcolor: primary,
            border: `1.5px solid ${primary}`,
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: 0.2,
            boxShadow: darkMode
              ? "0 2px 8px rgba(0,0,0,0.35)"
              : "0 2px 8px rgba(99,102,241,0.25)",
            transition:
              "transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              bgcolor: primaryStrong,
              borderColor: primaryStrong,
              transform: "translateY(-1px)",
              boxShadow: darkMode
                ? "0 4px 12px rgba(0,0,0,0.45)"
                : "0 4px 12px rgba(99,102,241,0.35)",
            },
            "&:focus-visible": {
              outline: `2px solid ${primaryStrong}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.22)",
            }}
          >
            <Plus size={14} strokeWidth={2.5} />
          </Box>
          {t("attic_add_more_pages", { ns: "attic" })}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: primaryStrong,
              }}
            >
              {t("attic_add_page", { ns: "attic" })}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              aria-label={t("attic_close_add_page", { ns: "attic" })}
              sx={{
                color: primary,
                p: 0.5,
                "&:hover": {
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.06)"
                    : "var(--primary-100)",
                  color: primaryStrong,
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            {types.map((type) => {
              const { label, Icon } = chipMeta[type];
              return (
                <Chip
                  key={type}
                  icon={<Icon size={14} />}
                  label={label}
                  onClick={() => handleSelect(type)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 30,
                    px: 0.5,
                    borderRadius: 999,
                    color: primaryStrong,
                    bgcolor: darkMode
                      ? "rgba(99, 102, 241, 0.12)"
                      : "var(--neutralWhite)",
                    border: `1px solid ${primaryBorderSoft}`,
                    transition:
                      "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                    "& .MuiChip-icon": {
                      color: primary,
                      ml: "8px",
                    },
                    "&:hover": {
                      borderColor: primary,
                      color: "var(--neutralWhite)",
                      bgcolor: primary,
                      "& .MuiChip-icon": {
                        color: "var(--neutralWhite)",
                      },
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
